import { SearchOutlined } from '@ant-design/icons';
import { Row, Col, Spin } from 'antd';
import { updateItem } from 'containers/App/selectors';
import { useScrollTop } from 'hooks';
import moment from 'moment';
import { resetSearchValue } from 'pages/Cpap/slices';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import {
  Text as MyText,
  HeaderTitle,
  BackToStepPrev,
  HeaderMini,
  MiniButton,
  RectangleItem,
  Rectangle,
} from 'components';
import { RootState } from '../../types';
import { getDataStoreFields } from '../Register/RegisterFirstStep/thunk';
import { resetItemUpdate } from './slices';
import Wrap from './styles';
import { getCPAPDetail } from './thunk';

type LocationState = {
  itemId: string;
};

const CpapDetail: FC = () => {
  useScrollTop();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { dataStoreFields } = useSelector((state: RootState) => state.register);
  const { cpap_detail, loading } = useSelector(updateItem);
  const { currentApplicationId } = useSelector((state: RootState) => state.workSpaces);

  useEffect(() => {
    if (location.state) {
      dispatch(getCPAPDetail(location.state.itemId));
    }
  }, [dispatch, location]);

  useEffect(() => {
    return () => {
      if (history.location.pathname !== '/cpap') {
        dispatch(resetSearchValue());
      }
      dispatch(resetItemUpdate());
    };
  }, [dispatch, history.location.pathname]);

  useEffect(() => {
    currentApplicationId &&
      dispatch(getDataStoreFields({ appId: currentApplicationId, dataStoreId: 't_cpap_instructions' }));
  }, [dispatch, currentApplicationId]);

  const selectAccessories = useMemo(() => {
    return dataStoreFields?.find(([_key, value]: [string, any]) => value.display_id === 'accessories')[1].options || [];
  }, [dataStoreFields]);

  const displayGender = useMemo(() => {
    const options: any[] =
      dataStoreFields?.find(([_key, value]: [string, any]) => value.display_id === 'gender')[1].options || [];
    const gender = options.find((f) => f.option_id === cpap_detail.gender);

    return gender ? gender.value : '';
  }, [dataStoreFields, cpap_detail.gender]);

  let yearCurrent = moment();
  let count = moment(cpap_detail.patient_birthday, 'YYYY-MM-DD');
  const age = yearCurrent.diff(count, 'year');
  const date = moment(cpap_detail.patient_birthday, 'YYYY-MM-DDThh:mm:ss.SSSZ').format('YYYY年MM月DD日');

  const backToList = () => {
    history.replace('/cpap/detail', { state: null });
    history.push('/cpap');
  };

  const checkAccessories = useCallback(
    (value: string): boolean => {
      if (!cpap_detail.accessories) {
        return false;
      }
      return cpap_detail.accessories.findIndex((f: string) => f === value) > -1;
    },
    [cpap_detail.accessories],
  );

  window.onbeforeunload = (event: any) => {
    event.preventDefault();
    history.replace('/cpap/detail', { state: null });
    history.push('/cpap');
  };

  if (!location.state) {
    return <Redirect to="/cpap" />;
  }

  return (
    <Wrap>
      <HeaderTitle title="CPAP指示書一覧・検索" icon={SearchOutlined} />
      <div className="backToScreenPrev">
        <BackToStepPrev backToPrev={backToList} />
      </div>
      <div className="cpap-body">
        {loading && (
          <div className="loading">
            <Spin size="large" />
          </div>
        )}
        <div className="headerMini">
          <HeaderMini text="CPAP指示書詳細" decor="spaceBetween">
            <MiniButton action={() => history.push('/cpap/edit', { itemId: cpap_detail.i_id })} text="編集する" />
          </HeaderMini>
        </div>
        <div className="section">
          <Row>
            <Col span={11} className="largeBox">
              <Rectangle text="医療機関" />
              <div className="box">
                <MyText
                  fontLeft="smallBoldFont"
                  fontRight="smallBoldFont"
                  padding="15px 2px"
                  textLeft="医療機関名"
                  textRight={cpap_detail.lookup_items?.facility_name?.facility_name}
                />
                <MyText
                  fontRight="smallFont"
                  fontLeft="smallBoldFont"
                  padding="15px 2px"
                  textLeft="所在地"
                  textRight={`${
                    cpap_detail.lookup_items?.facility_name?.facility_zip
                      ? `〒${cpap_detail.lookup_items?.facility_name?.facility_zip}`
                      : ''
                  }  ${cpap_detail.lookup_items?.facility_name?.facility_address1 || ''}  ${
                    cpap_detail.lookup_items?.facility_name?.facility_address2 || ''
                  }`}
                />
                <MyText
                  padding="15px 2px"
                  textLeft="TEL"
                  textRight={cpap_detail.lookup_items?.facility_name?.facility_tel}
                  fontLeft="smallBoldFont"
                  fontRight="smallFont"
                />
                <MyText
                  padding="15px 2px"
                  textLeft="営業担当"
                  textRight={cpap_detail.lookup_items?.facility_name?.in_charge_of_sales}
                  fontLeft="smallBoldFont"
                  fontRight="smallFont"
                />
                <MyText
                  padding="15px 2px"
                  textLeft="事務担当"
                  textRight={cpap_detail?.clerk}
                  fontLeft="smallBoldFont"
                  fontRight="smallFont"
                />
                <MyText
                  padding="15px 2px"
                  textLeft="転院履歴"
                  textRight={cpap_detail?.transfer_history}
                  fontLeft="smallBoldFont"
                  fontRight="smallFont"
                  isBorder={false}
                  whiteSpaceRight
                />
              </div>
            </Col>
            <Col span={11} offset={2} className="largeBox">
              <RectangleItem text="療養者" />
              <div className="box">
                <MyText
                  padding="15px 2px"
                  textLeft="お名前"
                  textRight={
                    cpap_detail?.patient_name
                      ? `${cpap_detail.patient_name} ${
                          cpap_detail.patient_name_kana ? `(${cpap_detail.patient_name_kana})` : ''
                        }`
                      : ''
                  }
                  fontLeft="smallBoldFont"
                  fontRight="smallBoldFont patient-name"
                />
                <MyText
                  padding="15px 2px"
                  textLeft="性別"
                  textRight={displayGender}
                  fontLeft="smallBoldFont"
                  fontRight="smallFont"
                />
                <MyText
                  padding="15px 2px"
                  textLeft="生年月日"
                  textRight={date === 'Invalid date' ? '' : `${date} (${age}歳)`}
                  fontLeft="smallBoldFont"
                  fontRight="smallFont"
                />
                <MyText
                  padding="15px 2px"
                  textLeft="設置住所"
                  textRight={
                    cpap_detail?.zip_code
                      ? `〒${cpap_detail.zip_code} ${cpap_detail.address_1} ${cpap_detail.address_2}`
                      : ''
                  }
                  fontLeft="smallBoldFont"
                  fontRight="smallFont"
                />
                <MyText
                  padding="15px 2px"
                  textLeft="TEL"
                  textRight={`${
                    cpap_detail?.phone_number ||
                    (cpap_detail?.mobile_phone_number ? `【携帯】${cpap_detail.mobile_phone_number}` : '')
                  } ${
                    cpap_detail?.phone_number && cpap_detail?.mobile_phone_number
                      ? `/ 【携帯】 ${cpap_detail?.mobile_phone_number}`
                      : ''
                  }`}
                  fontLeft="smallBoldFont"
                  fontRight="smallFont"
                />
                <MyText
                  padding="15px 2px"
                  textLeft="患者ID"
                  textRight={cpap_detail?.patient_id}
                  fontLeft="smallBoldFont"
                  fontRight="smallBoldFont"
                  isBorder={false}
                />
              </div>
            </Col>
          </Row>
          <div className="largeBox mt-40">
            <Rectangle text="設 定" />
            <div className="box">
              <Row className="content1">
                <Col className="standardBoldFont alignCenter" span={4}>
                  基本設定
                </Col>
                <Col span={18} className="d-flex alignCenter">
                  <p className="smallFont mr-15 mb-0">圧力</p>
                  <p className="standardBoldFont mh-10  mb-0">{cpap_detail?.pressure_min}</p>
                  <p className="standardBoldFont  mb-0">〜</p>
                  <p className="standardBoldFont mh-10  mb-0">{cpap_detail?.pressure_max}</p>
                  <p className="smallFont  mb-0">cmH2O</p>
                  <div className="bar  mb-0" />
                  <p className="smallFont mr-15  mb-0">ランプ時間</p>
                  <p className="standardBoldFont mh-10  mb-0">{cpap_detail?.lamp_time}</p>
                  <p className="smallFont  mb-0">分</p>
                  <div className="bar  mb-0" />
                  <p className="smallFont mr-15  mb-0">最大ランプ時間</p>
                  <p className="standardBoldFont mh-10  mb-0">{cpap_detail?.lamp_time_max}</p>
                  <p className="smallFont  mb-0">分</p>
                </Col>
              </Row>
              <Row className="content2">
                <Col span={4} className="d-flex alignCenter standardBoldFont">
                  機種
                </Col>
                <Col span={20}>
                  <Row className="border-bt" style={{ paddingBottom: '6px' }}>
                    <Col span={3}>
                      <span>{cpap_detail?.machine}</span>
                    </Col>
                    <Col span={4}>{cpap_detail.machine_other}</Col>
                    <Col span={2}>機番</Col>
                    <Col span={7}>
                      <span>{cpap_detail?.machine_no}</span>
                    </Col>
                  </Row>
                  <Row style={{ paddingTop: '6px' }}>
                    <Col span={2}>
                      <span className="middleBoldFont">EPR</span>
                    </Col>
                    <Col span={5}>
                      <span>{cpap_detail?.machine_epr_no}</span>
                    </Col>
                    <Col span={2} className="middleBoldFont">
                      DN/キー
                    </Col>
                    <Col span={5}>
                      <span>{cpap_detail?.machine_dn_no}</span>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="content2">
                <Col span={4} className="d-flex alignCenter standardBoldFont">
                  付属品
                </Col>
                <Col span={20}>
                  <Row className="bb-1 mb-6px">
                    <Col span={3} className="d-flex alignCenter middleBoldFont">
                      <span className="standardFont">{selectAccessories[0]?.value}</span>
                    </Col>
                    <Col span={8} className="d-flex alignCenter">
                      <span className="standardFont">種類</span>
                      <span className="ml-15px standardBoldFont ml-8">
                        {cpap_detail?.acc_mask !== 'その他' ? cpap_detail.acc_mask : cpap_detail.acc_mask_other}
                      </span>
                    </Col>
                    <Col span={8} className="alignCenter">
                      <div className="l space2" />
                      <span className="ml-15px standardFont">サイズ</span>
                      <span className="ml-15px standardBoldFont ml-8">
                        {cpap_detail?.acc_mask_size !== 'その他'
                          ? cpap_detail.acc_mask_size
                          : cpap_detail.acc_mask_size_other}
                      </span>
                    </Col>
                  </Row>
                  <Row className="bb-1">
                    <Col
                      span={3}
                      className={
                        checkAccessories(selectAccessories[1]?.display_id)
                          ? `d-flex alignCenter middleBoldFont`
                          : 'd-flex alignCenter accessories-disable'
                      }
                    >
                      <span className="standardFont">{selectAccessories[1]?.value}</span>
                    </Col>
                    {checkAccessories(selectAccessories[1]?.display_id) && (
                      <Col span={5}>
                        <span className="standardFont">加湿器機番</span>
                        <span className="ml-15px standardFont">{cpap_detail.humidifier_machine_no ?? ''}</span>
                      </Col>
                    )}
                    <Col
                      span={3}
                      className={
                        checkAccessories(selectAccessories[2]?.display_id)
                          ? `d-flex alignCenter middleBoldFont`
                          : 'd-flex alignCenter accessories-disable'
                      }
                    >
                      <span className="standardFont">{selectAccessories[2]?.value}</span>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="content1">
                <Col className="standardBoldFont" span={4}>
                  次回定期送付月
                </Col>
                <Col className="d-flex standardBoldFont">
                  {moment(
                    `${cpap_detail.last_month?.slice(0, 4)}-${cpap_detail.last_month?.slice(4)}`,
                    'YYYY-MM',
                  ).format('YYYY年MM月') === 'Invalid date'
                    ? ''
                    : moment(
                        `${cpap_detail.last_month?.slice(0, 4)}-${cpap_detail.last_month?.slice(4)}`,
                        'YYYY-MM',
                      ).format('YYYY年MM月')}
                </Col>
                <Col span={3} className="l space2" />
                <Col className="d-flex standardBoldFont">
                  {moment(cpap_detail.collection_date, 'YYYY-MM-DDThh:mm:ss').format('MM月DD日') === 'Invalid date'
                    ? ''
                    : moment(cpap_detail.collection_date, 'YYYY-MM-DDThh:mm:ss').format('MM月DD日')}
                </Col>
              </Row>
              <Row className="content1">
                <Col className="standardBoldFont" span={4}>
                  設置日
                </Col>
                <Col span={20}>
                  <Row>
                    <Col span={16} className="d-flex">
                      <p className="mb-0 mr-15 standardFont">設置日</p>
                      <p className="mb-0 standardBoldFont">
                        {moment(cpap_detail.installation_date, 'YYYY-MM-DDThh:mm:ss').format('YYYY年MM月DD日') ===
                        'Invalid date'
                          ? ''
                          : moment(cpap_detail.installation_date, 'YYYY-MM-DDThh:mm:ss').format('YYYY年MM月DD日')}
                      </p>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="content2">
                <Col className="standardBoldFont d-flex alignCenter" span={4}>
                  備考
                </Col>
                <Col className="standardFont white-space" span={20}>
                  {cpap_detail?.cpap_remark}
                </Col>
              </Row>
              <Row className="content2">
                <Col className="standardBoldFont d-flex alignCenter" span={4}>
                  受診明細用備考
                </Col>
                <Col className="standardFont white-space" span={20}>
                  {cpap_detail?.remark_for_output}
                </Col>
              </Row>
              <Row className="content2">
                <Col className="standardBoldFont d-flex alignCenter" span={4}>
                  利用停止
                </Col>
                <Col className="standardFont" span={20}>
                  {Number(cpap_detail.suspension_flg) === 1 ? '利用停止' : ''}
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </div>
    </Wrap>
  );
};

export default CpapDetail;
